import React, { useMemo } from 'react';
import { ChartColor } from '@t-one-internal/design-system';

// Support snake_case (API), camelCase, and partial-camel (snakeToCamel keeps _360)
interface FeedbackFlowData {
  selfReviewsDone?: number;
  self_reviews_done?: number;
  managerReviewsDone?: number;
  manager_reviews_done?: number;
  reviews360Done?: number;
  reviews_360_done?: number;
  reviews_360Done?: number;
  reviews360Pending?: number;
  reviews_360_pending?: number;
  reviews_360Pending?: number;
  selfCompletionPct?: number;
  self_completion_pct?: number;
  managerCompletionPct?: number;
  manager_completion_pct?: number;
  completion360Pct?: number;
  completion_360_pct?: number;
  completion_360Pct?: number;
  [key: string]: number | undefined;
}

interface FeedbackFlowWidgetProps {
  data?: FeedbackFlowData | FeedbackFlowData[] | { data: FeedbackFlowData[] };
  loading?: boolean;
  error?: string;
}

// Helper to get first item from various data formats
const extractData = (data: FeedbackFlowWidgetProps['data']): FeedbackFlowData | null => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if ((data as { data: FeedbackFlowData[] })?.data) {
    const arr = (data as { data: FeedbackFlowData[] }).data;
    return arr[0] || null;
  }
  return data as FeedbackFlowData;
};

// Helpers for snake_case/camelCase compatibility
const getSelfReviewsDone = (d: FeedbackFlowData) =>
  Number(d.selfReviewsDone ?? d.self_reviews_done ?? 0);
const getManagerReviewsDone = (d: FeedbackFlowData) =>
  Number(d.managerReviewsDone ?? d.manager_reviews_done ?? 0);
const get360Done = (d: FeedbackFlowData) =>
  Number(d.reviews_360Done ?? d.reviews360Done ?? d.reviews_360_done ?? 0);
const get360Pending = (d: FeedbackFlowData) =>
  Number(d.reviews_360Pending ?? d.reviews360Pending ?? d.reviews_360_pending ?? 0);
const getSelfPct = (d: FeedbackFlowData) =>
  Number(d.selfCompletionPct ?? d.self_completion_pct ?? 0);
const getManagerPct = (d: FeedbackFlowData) =>
  Number(d.managerCompletionPct ?? d.manager_completion_pct ?? 0);
const get360Pct = (d: FeedbackFlowData) =>
  Number(d.completion_360Pct ?? d.completion360Pct ?? d.completion_360_pct ?? 0);

interface KPICardProps {
  value: number;
  label: string;
  valueColor: string;
  bgColor: string;
}

const KPICard: React.FC<KPICardProps> = ({ value, label, valueColor, bgColor }) => (
  <div
    style={{
      backgroundColor: bgColor,
      borderRadius: '12px',
      padding: '20px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '90px',
    }}
  >
    <div
      style={{
        fontSize: '32px',
        fontWeight: 700,
        color: valueColor,
        lineHeight: 1.1,
      }}
    >
      {value.toLocaleString()}
    </div>
    <div
      style={{
        fontSize: '12px',
        fontWeight: 500,
        color: '#6B7280',
        marginTop: '6px',
        textAlign: 'center',
      }}
    >
      {label}
    </div>
  </div>
);

export const FeedbackFlowWidget: React.FC<FeedbackFlowWidgetProps> = ({
  data,
  loading,
  error,
}) => {
  const renderHeader = () => (
    <div className="flex items-center gap-2 mb-4">
      <h3 className="font-semibold text-gray-900 text-sm">Feedback Flow Metrics</h3>
      <span className="text-xs text-gray-400">Across all active cycles</span>
    </div>
  );
  const metrics = useMemo(() => extractData(data), [data]);

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          No feedback flow data available
        </div>
      </div>
    );
  }

  const selfDone = getSelfReviewsDone(metrics);
  const managerDone = getManagerReviewsDone(metrics);
  const done360 = get360Done(metrics);
  const pending360 = get360Pending(metrics);
  const selfPct = getSelfPct(metrics);
  const managerPct = getManagerPct(metrics);
  const pct360 = get360Pct(metrics);

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader()}

      {/* KPI Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          flex: 1,
        }}
      >
        <KPICard
          value={selfDone}
          label="Self Reviews Done"
          valueColor={ChartColor.violet}
          bgColor="#F3F4F6"
        />
        <KPICard
          value={managerDone}
          label="Manager Reviews Done"
          valueColor={ChartColor.emerald}
          bgColor="#D1FAE5"
        />
        <KPICard
          value={done360}
          label="360 Reviews Done"
          valueColor={ChartColor.orange}
          bgColor="#EDE9FE"
        />
        <KPICard
          value={pending360}
          label="360 Pending"
          valueColor={ChartColor.amber}
          bgColor="#FEF3C7"
        />
      </div>

      {/* Completion Ratio Footer */}
      <div
        style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: '1px solid #F3F4F6',
          fontSize: '13px',
          color: '#374151',
        }}
      >
        <span style={{ fontWeight: 500 }}>Completion ratio:</span>{' '}
        <span style={{ color: ChartColor.violet, fontWeight: 600 }}>Self {selfPct}%</span>
        <span style={{ color: '#9CA3AF' }}> · </span>
        <span style={{ color: ChartColor.emerald, fontWeight: 600 }}>Manager {managerPct}%</span>
        <span style={{ color: '#9CA3AF' }}> · </span>
        <span style={{ color: ChartColor.orange, fontWeight: 600 }}>360 {pct360}%</span>
      </div>
    </div>
  );
};

export default FeedbackFlowWidget;
